import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, CheckCircle, AlertCircle } from 'lucide-react';

const GOLD = 'var(--brand-primary)';

interface FeedbackInfo {
  id: string;
  type: string;
  beantwoord: boolean;
  dossierNummer?: string;
  klantNaam?: string;
}

const TYPE_LABELS: Record<string, string> = {
  NA_OPMETING: 'uw opmeting',
  NA_LEVERING: 'uw levering',
  NA_PLAATSING: 'uw plaatsing',
  NA_SERVICE: 'uw servicebezoek',
};

export function PubliekeFeedbackPage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<FeedbackInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [opmerking, setOpmerking] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`/api/public/feedback/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.message) throw new Error(data.message);
        setInfo(data);
        if (data.beantwoord) setSubmitted(true);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSubmit() {
    if (!score) return;
    setSubmitting(true);
    try {
      const r = await fetch(`/api/public/feedback/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, opmerking }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fout bij indienen');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: GOLD }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full text-center">
          <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link niet gevonden</h2>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full text-center">
          <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Bedankt voor uw feedback!</h2>
          <p className="text-gray-500 text-sm">Uw beoordeling helpt ons onze service te verbeteren.</p>
        </div>
      </div>
    );
  }

  const typeLabel = TYPE_LABELS[info?.type || ''] || 'uw ervaring';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: GOLD }}>
            <Star size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Hoe was {typeLabel}?</h1>
          {info?.klantNaam && (
            <p className="text-gray-500 text-sm">Beste {info.klantNaam}, uw mening is belangrijk voor ons.</p>
          )}
        </div>

        {/* Star rating */}
        <div className="flex justify-center gap-3 mb-8">
          {[1, 2, 3, 4, 5].map(s => (
            <button
              key={s}
              onMouseEnter={() => setHovered(s)}
              onMouseLeave={() => setHovered(0)}
              onClick={() => setScore(s)}
              className="transition-transform hover:scale-110"
            >
              <Star
                size={40}
                fill={(hovered || score) >= s ? GOLD : 'none'}
                color={(hovered || score) >= s ? GOLD : '#D1D5DB'}
                strokeWidth={1.5}
              />
            </button>
          ))}
        </div>

        {score > 0 && (
          <div className="text-center mb-4">
            <span className="text-sm font-medium text-gray-700">
              {score === 1 ? 'Zeer slecht' : score === 2 ? 'Slecht' : score === 3 ? 'Matig' : score === 4 ? 'Goed' : 'Uitstekend'}
            </span>
          </div>
        )}

        {/* Opmerking */}
        <div className="mb-6">
          <label className="text-sm text-gray-600 mb-2 block">Opmerking (optioneel)</label>
          <textarea
            value={opmerking}
            onChange={e => setOpmerking(e.target.value)}
            placeholder="Vertel ons meer over uw ervaring..."
            rows={4}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!score || submitting}
          className="w-full py-3 rounded-xl text-white font-medium text-sm disabled:opacity-50 transition-opacity"
          style={{ backgroundColor: GOLD }}
        >
          {submitting ? 'Bezig...' : 'Beoordeling versturen'}
        </button>
      </div>
    </div>
  );
}
